import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { Toolbar } from "primereact/toolbar";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { MultiSelect } from "primereact/multiselect";
import { useEffect, useRef, useState } from "react";
import { HolidayListColumns } from "../../appconfig/DatatableSetting";
import {
  provinces,
  defaultPageRowOptions,
  defaultRowOptions,
  paginatorLinks,
  showingEntries,
  SkeletonbodyTemplate,
  Skeletonitems,
} from "../../appconfig/Settings";
import { dateTemplate } from "../../appconfig/TemplateFieldSetting";
import AppBreadcrumb from "../../components/CustomeBreadcrumb/AppBreadcrumb";
import {
  ADD_HOLIDAY,
  DELETE_HOLIDAY,
  GET_HOLIDAY_LIST,
  UPDATE_HOLIDAY,
} from "../../config/ApiConstant";
import { HolidayValidate } from "../../config/Validate";
import {
  deleteData,
  getSpecificItemDetails,
  postAllData,
  updateData,
} from "../../service/ProductService";
import { Tag } from "primereact/tag";
import moment from "moment";

const ManageHolidays = () => {
  document.title = "Holidays | Nexawage Payroll";
  const toast = useRef<any>(null);
  const [globalFilter, setGlobalFilter] = useState<any>(null);
  const [globalFieldFilter, setGlobalFieldFilter] = useState<any>(null);
  const [pageLoad, setPageLoad] = useState<boolean>(false);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [holidayList, setHolidayList] = useState<any>([]);
  const [deleteDialog, setDeleteDialog] = useState<boolean>(false);
  const [addDeleteHolidayDialog, setAddDeleteHolidayDialog] =
    useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<any>(null);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [errors, setErrors] = useState<any>({});
  const [globleHolidayData, setGlobalHolidayData] = useState<any>({});

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);

  useEffect(() => {
    getHolidayList();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [selectedYear]);

  // holiday list api call
  const getHolidayList = async () => {
    try {
      const response = await getSpecificItemDetails(
        GET_HOLIDAY_LIST,
        "year",
        selectedYear
      );

      const data = response?.data;

      if (data) {
        setHolidayList(data);
        setPageLoad(true);
      } else {
        setHolidayList([]);
        setPageLoad(false);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
      setPageLoad(false);
    }
  };

  // open add or edit holiday dialog
  const addEditDialog = (rowData: any = null) => {
    if (rowData) {
      setGlobalHolidayData({
        ...rowData,
        province: Array.isArray(rowData.province)
          ? rowData.province
          : rowData.province
          ? rowData.province.split(",").filter((p: any) => p && p.trim() !== "")
          : [],
      });
      setIsEdit(true);
    } else {
      setGlobalHolidayData({});
      setIsEdit(false);
    }
    setErrors({});
    setAddDeleteHolidayDialog(true);
  };

  // hide add/edit dialog
  const hideAddEditUserDialog = () => {
    setAddDeleteHolidayDialog(false);
    setSubmitLoading(false);
  };

  // left toolbar template
  const leftToolbarTemplate = () => (
    <div className="flex flex-wrap gap-3 align-items-center">
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          type="search"
          value={globalFilter || ""}
          onInput={(e) => setGlobalFilter(e.currentTarget.value)}
          placeholder="Search..."
        />
      </span>
    </div>
  );

  // right toolbar template
  const rightToolbarTemplate = () => (
    <>
      <Calendar
        value={new Date(selectedYear, 0)}
        view="year"
        dateFormat="yy"
        onChange={(e: any) => {
          const year = e.value?.getFullYear?.();
          if (year) setSelectedYear(year);
        }}
        showIcon
        placeholder="Select Year"
        className="ml-1 sm:ml-2 w-11rem"
      />
      <Button
        className="ml-1 sm:ml-2 common-primary-button"
        icon="pi pi-plus"
        label="Add Holiday"
        onClick={() => addEditDialog()}
      />
    </>
  );

  // confirmation for delete
  const confirmDelete = (deleteID: any) => {
    setDeleteId(deleteID);
    setDeleteDialog(true);
  };

  // handle delete dialog
  const hideDeleteDialog = () => {
    setDeleteDialog(false);
    setDeleteId(null);
  };

  // action body template
  const actionBodyTemplate = (rowData: any) => (
    <div className="actions">
      <Button
        icon="pi pi-pencil"
        tooltip="Edit"
        className="common-edit-btn"
        tooltipOptions={{ position: "bottom" }}
        onClick={() => addEditDialog(rowData)}
      />
      <Button
        icon="pi pi-trash"
        tooltipOptions={{ position: "bottom" }}
        className="ml-2 p-button-danger common-delete-btn"
        tooltip="Delete"
        onClick={() => confirmDelete(rowData.id)}
      />
    </div>
  );

  // input value hanlding
  const onInputChange = (e: any, name: string) => {
    let val = e.target?.value ?? e.value;

    // normalise date and save date
    if (name === "date" && val) {
      val = moment(val).format("YYYY-MM-DD");
    }

    setGlobalHolidayData({ ...globleHolidayData, [name]: val });
    setErrors({ ...errors, [name]: "" });
  };

  // submit holiday api call
  const submitHolidayForm = async () => {
    const { isError, errors } = HolidayValidate(globleHolidayData);
    setErrors(errors);
    if (isError) return;

    setSubmitLoading(true);

    const payload = {
      name: globleHolidayData?.name,
      date: globleHolidayData?.date
        ? moment(globleHolidayData.date).format("YYYY-MM-DD")
        : null,
      province: globleHolidayData?.province?.join(",") || "",
    };

    try {
      const response = isEdit
        ? await updateData(UPDATE_HOLIDAY, globleHolidayData?.id, payload)
        : await postAllData(ADD_HOLIDAY, payload);

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message,
        });
        hideAddEditUserDialog();
        getHolidayList();
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  // delete holiday api call
  const deleteHoliday = async () => {
    try {
      setSubmitLoading(true);
      const response = await deleteData(DELETE_HOLIDAY, deleteId);
      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Deleted",
          detail: response.message,
        });
        hideDeleteDialog();
        getHolidayList();
        setSubmitLoading(false);
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message,
      });
      setSubmitLoading(false);
    }
  };

  // holiday name with province template
  const holidayNameWithProvincesTemplate = (rowData: any) => {
    const holidayName = rowData.name;
    const provincesArray = Array.isArray(rowData.province)
      ? rowData.province
      : rowData.province?.split?.(",") || [];

    return (
      <div className="flex gap-1 flex-column">
        <div className="">{holidayName}</div>
        <div className="flex flex-wrap gap-1 justify-content-center">
          {provincesArray.map((prov: string, index: number) => (
            <Tag key={index} value={prov.trim()} severity="info" />
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <AppBreadcrumb
        currentMenu="Manage Holiday"
        pageName="Holiday Lists"
        pageTitle="Holiday Lists"
      />
      <div className="grid crud-demo">
        <div className="col-12">
          <Card className="bg-white lg:p-2 crud-demo">
            <Toast ref={toast} />
            <Toolbar
              className="gap-3"
              left={leftToolbarTemplate}
              right={rightToolbarTemplate}
            />
            {pageLoad ? (
              <DataTable
                value={holidayList}
                showGridlines
                dataKey="id"
                responsiveLayout="scroll"
                stripedRows
                globalFilter={globalFilter}
                rows={defaultRowOptions}
                paginator={holidayList?.length > 0}
                rowsPerPageOptions={defaultPageRowOptions}
                className="datatable-responsive p-datatble-sm"
                paginatorTemplate={paginatorLinks}
                currentPageReportTemplate={showingEntries + " Holidays"}
                emptyMessage="No Holidays Found"
                filters={globalFieldFilter}
                onFilter={(e) => setGlobalFieldFilter(e.filters)}
                removableSort
              >
                {HolidayListColumns.map((col) => {
                  if (col.field === "name") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        body={holidayNameWithProvincesTemplate}
                      />
                    );
                  } else if (col.field === "date") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        sortable
                        body={dateTemplate}
                      />
                    );
                  } else if (col.field === "action") {
                    return (
                      <Column
                        key={col.field}
                        field={col.field}
                        header={col.header}
                        body={actionBodyTemplate}
                      />
                    );
                  }
                  return (
                    <Column
                      key={col.field}
                      field={col.field}
                      header={col.header}
                      filter
                      filterPlaceholder={col.header}
                    />
                  );
                })}
              </DataTable>
            ) : (
              <DataTable value={Skeletonitems}>
                {HolidayListColumns.map((col) => (
                  <Column
                    key={col.field}
                    field={col.field}
                    header={col.header}
                    body={SkeletonbodyTemplate}
                  />
                ))}
              </DataTable>
            )}

            {/* Add/Edit Dialog */}
            <Dialog
              visible={addDeleteHolidayDialog}
              style={{ width: "800px" }}
              draggable={false}
              className="mx-3 lg:mx-0"
              header={isEdit ? "Edit Holiday" : "Add Holiday"}
              modal
              footer={
                <div className="flex pt-2 align-items-center justify-content-end">
                  <Button
                    label="Cancel"
                    icon="pi pi-times"
                    className="mr-2 p-button-secondary"
                    onClick={hideAddEditUserDialog}
                  />
                  <Button
                    label="Submit"
                    icon="pi pi-check"
                    onClick={submitHolidayForm}
                    className="common-primary-button"
                    loading={submitLoading}
                  />
                </div>
              }
              onHide={hideAddEditUserDialog}
            >
              <div className="grid p-fluid formgrid">
                <div className="field col-12 md:col-6">
                  <label htmlFor="name">Holiday Name *</label>
                  <InputText
                    id="name"
                    value={globleHolidayData.name || ""}
                    name="name"
                    autoComplete="off"
                    placeholder="Enter Holiday Name"
                    onChange={(e) => onInputChange(e, "name")}
                    className={errors["name"] && "p-invalid"}
                  />
                  <small className="p-invalid-txt">{errors["name"]}</small>
                </div>

                {/* Holiday Date */}
                <div className="field col-12 md:col-6">
                  <label htmlFor="date">Holiday Date *</label>
                  <Calendar
                    value={
                      globleHolidayData.date
                        ? moment(globleHolidayData.date, "YYYY-MM-DD").toDate()
                        : null
                    }
                    dateFormat="dd/mm/yy"
                    placeholder="Enter Holiday Date"
                    onChange={(e) => onInputChange(e, "date")}
                    className={errors["date"] && "p-invalid"}
                    showIcon
                  />

                  <small className="p-invalid-txt">{errors["date"]}</small>
                </div>
                {/* Select Province */}
                <div className="field col-12 md:col-12">
                  <label htmlFor="province">
                    Provinces{" "}
                    <span className="text-change-note">
                      {" "}
                      (Note:- Select province only if holiday is
                      province-specific.)
                    </span>
                  </label>
                  <MultiSelect
                    inputId="province"
                    value={globleHolidayData.province || []}
                    options={provinces}
                    onChange={(e) => onInputChange(e, "province")}
                    placeholder="Select Provinces"
                    className={errors["province"] ? "p-invalid" : ""}
                  />
                  <small className="p-invalid-txt">{errors["province"]}</small>
                </div>
              </div>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog
              visible={deleteDialog}
              header="Delete Holiday"
              modal
              footer={
                <div className="flex pt-2 align-items-center justify-content-end">
                  <Button
                    label="Cancel"
                    icon="pi pi-times"
                    className="mr-2 p-button-secondary"
                    onClick={hideDeleteDialog}
                  />
                  <Button
                    label="Delete"
                    icon="pi pi-trash"
                    className="p-button-danger"
                    onClick={deleteHoliday}
                    loading={submitLoading}
                  />
                </div>
              }
              onHide={hideDeleteDialog}
            >
              <div className="flex align-items-center justify-content-start">
                <i
                  className="mr-3 pi pi-exclamation-triangle delete-triangle"
                  style={{ fontSize: "2rem" }}
                />
                <span className="delete-dialog-note">
                  Are you sure you want to delete this holiday?
                </span>
              </div>
            </Dialog>
          </Card>
        </div>
      </div>
    </>
  );
};

export default ManageHolidays;
